import { z } from 'zod';
import { ProceduralActionSchema, PROCEDURAL_ACTIONS_MAX } from './procedural-actions';
/**
 * object-library-v2 — ownership scope of a library object.
 *   public    — anyone can see + (if listed) buy it.
 *   operator  — bound to the uploading operator (`ownerOperatorId`),
 *               usable on all their characters; only their surfaces show it.
 *   character — bound to ONE character (`characterId`); only that
 *               character's scene + storefront context shows it.
 */
export const ObjectScopeEnum = z.enum(['public', 'operator', 'character']);
/**
 * object-library-v2 — front-stage status (replaces the implicit
 * "price 0 = hidden" rule).
 *   listed  — shown on the fan storefront AND buyable (requires price > 0).
 *   preview — shown on the storefront but NOT buyable (display only).
 *   backend — hidden from fans; operator/admin attach only via scene editor.
 */
export const FrontStatusEnum = z.enum(['listed', 'preview', 'backend']);
/**
 * object-library-v2 — single source for the read-time front-status
 * default. A priced object defaults to `listed`, an unpriced one to
 * `preview`. Used by BOTH the create path and the DDB read mapper
 * (`normalizeRecord`) so legacy rows (no stored `frontStatus`) read
 * consistently with current behaviour and never become silently
 * buyable-at-0.
 */
export function defaultFrontStatus(pricePoints) {
    return (pricePoints ?? 0) > 0 ? 'listed' : 'preview';
}
/**
 * Cross-character library object — see the `shared-object-library`
 * capability spec. Stored in DDB table `nx-shared-objects-<env>` with
 * S3 file at `s3://nx-character-assets-<env>/_library/<objectId>/<file>`.
 *
 * Lifecycle:
 *   1. Operator uploads via `POST /api/library/objects/upload-url` → PUT
 *      to S3 directly → `POST /api/library/objects` to register DDB.
 *   2. Characters reference this entry via
 *      `model3d.sceneObjects[].libraryId === objectId`. The character
 *      record's own `modelPath` field is set to this library item's
 *      `modelPath` at reference-time so deploy bake + runtime loader
 *      don't have to know libraryId exists.
 *   3. Deletion is a two-phase confirm — first call returns the list of
 *      referencing characters; second call (?cascade=true) removes those
 *      references AND deletes the S3 file + DDB record.
 *
 * Hard caps:
 *   - label: ≤40 chars
 *   - size: ≤10 MB (10 * 1024 * 1024 = 10485760 bytes)
 *   - tags: ≤8 entries, each ≤24 chars
 */
export const SharedObjectSchema = z.object({
    objectId: z.string().uuid(),
    label: z.string().min(1).max(40),
    modelPath: z.string().min(1),
    modelFormat: z.enum(['glb', 'fbx']),
    size: z.number().int().positive().max(50 * 1024 * 1024),
    tags: z.array(z.string().min(1).max(24)).max(8).default([]),
    /** object-library-v2: auto-generated snapshot PNG URL captured at
     *  upload time (admin SimpleGlbViewer canvas → PNG → upload-url). The
     *  fan storefront renders this as the card preview; falls back to a
     *  placeholder on error. (Was RESERVED/unused in v1.) */
    thumbnail: z.string().url().optional(),
    /** Cognito sub of the operator who first created this library entry. */
    createdBy: z.string().min(1).max(120),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    /**
     * Per the operator's monetisation intent — every library object can
     * carry a point price. Fans pay this many points to acquire the
     * object (currency-agnostic 1:1 — see PurchaseFanObject endpoint).
     * Zero / undefined = not yet listed for sale (operator can still
     * reference it on their own characters, fans just can't buy).
     * Range matches modelDistribution.pricePoints — 1..100000.
     */
    pricePoints: z.number().int().min(0).max(100000).default(0),
    /**
     * Per scene-objects-and-attachments v2: library entries can ship with
     * a default texture override + procedural breath actions. When a
     * character references this library item, the *character's* SceneObject
     * is initialised with these defaults; the operator can still override
     * them on the character side, but the library acts as the source of
     * truth for "what does this prop look like / move like".
     *
     * Mirrors the same fields on SceneObjectSchema 1:1.
     */
    texture: z.string().url().optional(),
    proceduralActions: z.array(ProceduralActionSchema).max(PROCEDURAL_ACTIONS_MAX).default([]),
    // ── object-library-v2 ───────────────────────────────────────────
    /** Ownership scope (chosen at upload). Defaults to `public` so legacy
     *  rows (no stored value) read as global, preserving v1 behaviour. */
    scope: ObjectScopeEnum.default('public'),
    /** Owning operator's Cognito sub when `scope === 'operator'`. Defaults
     *  to the object's `createdBy` at create time (no operator-profile link). */
    ownerOperatorId: z.string().min(1).max(120).optional(),
    /** Bound character id when `scope === 'character'`. */
    characterId: z.string().min(1).optional(),
    /** Front-stage status. OPTIONAL with no static default — the read
     *  mapper (`normalizeRecord`) fills it via `defaultFrontStatus(price)`
     *  so a legacy row's absence is distinguishable and migrates
     *  correctly (priced → listed, unpriced → preview). */
    frontStatus: FrontStatusEnum.optional(),
    /** Optional fan-facing description shown in the storefront detail view. */
    description: z.string().max(500).optional(),
}).superRefine((v, ctx) => {
    // A `listed` object must be priced (otherwise it'd be "buyable" at 0).
    if (v.frontStatus === 'listed' && (v.pricePoints ?? 0) <= 0) {
        ctx.addIssue({
            code: 'custom',
            path: ['frontStatus'],
            message: 'frontStatus "listed" requires pricePoints > 0',
        });
    }
    // A `character`-scoped object must name its bound character.
    if (v.scope === 'character' && !v.characterId) {
        ctx.addIssue({
            code: 'custom',
            path: ['characterId'],
            message: 'scope "character" requires characterId',
        });
    }
});
/** Draft shape accepted by `POST /api/library/objects` — server fills
 *  in objectId / createdBy / createdAt / updatedAt. */
export const SharedObjectCreateSchema = z.object({
    objectId: z.string().uuid(),
    label: z.string().min(1).max(40),
    modelPath: z.string().min(1),
    modelFormat: z.enum(['glb', 'fbx']),
    size: z.number().int().positive().max(50 * 1024 * 1024),
    tags: z.array(z.string().min(1).max(24)).max(8).default([]),
    pricePoints: z.number().int().min(0).max(100000).default(0),
    texture: z.string().url().optional(),
    proceduralActions: z.array(ProceduralActionSchema).max(PROCEDURAL_ACTIONS_MAX).default([]),
    // object-library-v2 — client supplies these at upload.
    scope: ObjectScopeEnum.default('public'),
    ownerOperatorId: z.string().min(1).max(120).optional(),
    characterId: z.string().min(1).optional(),
    frontStatus: FrontStatusEnum.optional(),
    description: z.string().max(500).optional(),
    /** Auto-generated snapshot PNG URL captured client-side at upload. */
    thumbnail: z.string().url().optional(),
}).superRefine((v, ctx) => {
    if (v.frontStatus === 'listed' && (v.pricePoints ?? 0) <= 0) {
        ctx.addIssue({ code: 'custom', path: ['frontStatus'], message: 'frontStatus "listed" requires pricePoints > 0' });
    }
    if (v.scope === 'character' && !v.characterId) {
        ctx.addIssue({ code: 'custom', path: ['characterId'], message: 'scope "character" requires characterId' });
    }
});
/** Patch shape for `PATCH /api/library/objects/{id}` — superadmin
 *  edits the operator-facing price post-creation. Only mutable
 *  fields are listed here; modelPath etc. are immutable after first
 *  upload. */
export const SharedObjectPatchSchema = z.object({
    label: z.string().min(1).max(40).optional(),
    tags: z.array(z.string().min(1).max(24)).max(8).optional(),
    pricePoints: z.number().int().min(0).max(100000).optional(),
    /** Set null to clear; omit to leave unchanged. */
    texture: z.string().url().nullable().optional(),
    proceduralActions: z.array(ProceduralActionSchema).max(PROCEDURAL_ACTIONS_MAX).optional(),
    // object-library-v2 — mutable post-creation. No cross-field refine
    // here (patch is partial); the merged record is re-validated against
    // SharedObjectSchema by saveLibraryObject, which enforces the refines.
    scope: ObjectScopeEnum.optional(),
    ownerOperatorId: z.string().min(1).max(120).nullable().optional(),
    characterId: z.string().min(1).nullable().optional(),
    frontStatus: FrontStatusEnum.optional(),
    description: z.string().max(500).nullable().optional(),
    thumbnail: z.string().url().nullable().optional(),
});
/** Request body for the upload-url endpoint. */
export const SharedObjectUploadUrlSchema = z.object({
    contentType: z.string().min(1).max(120),
    size: z.number().int().positive().max(50 * 1024 * 1024),
    /** v2: image extensions accepted for library-object texture override
     *  uploads. Path layout stays `_library/<objectId>/...<ext>`. */
    ext: z.enum(['glb', 'fbx', 'png', 'jpg', 'jpeg', 'webp']),
});
